import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss, Card, CardTitle } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  LEVEL_CODE_OPTIONS,
  RULE_OPTIONS,
  YN_OPTIONS,
  PROJECT_STRUCTURE_HELP,
  PROJECT_DOWNSTREAM_IMPACT_SCREENS,
  cloneProjectPreset,
  validateProjectStructure,
  loadProjectStructurePayload,
  saveProjectStructurePayload,
  sanitizeProjectStructureRows,
  buildBudgetLevelColumnFields,
  canUnconfirmProjectStructure,
} from 'budget/budgetProjectStructureStore';
import { migrateProjectYear, listProjectsForMigrate } from 'budget/budgetYearMigrate';
import YearMigrateDialog from 'components/budget/YearMigrateDialog';

/** 검색조건에서 제외한 목적구분 — 저장 키 호환용 고정값 */
const STRUCTURE_PURPOSE = '집행';

const GRID_ID = 'budget-project-structure';

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1.35fr 0.65fr;
  gap: 12px;
  flex: 1;
  min-height: 0;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

const PanelHead = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${theme.colors.border};
  font-weight: 700;
  font-size: ${theme.typography.fontSize.sm};
`;

const PreviewPath = styled.div`
  margin: 12px 14px;
  padding: 10px 12px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  font-weight: 700;
  font-size: ${theme.typography.fontSize.sm};
`;

const CheckList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 14px 14px;
`;

const CheckItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${theme.colors.borderLight || theme.colors.border};
  font-size: ${theme.typography.fontSize.sm};
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${theme.typography.fontSize.xs};
  font-weight: 700;
  background: ${(p) => (p.$ok ? '#D1FAE5' : '#FEE2E2')};
  color: ${(p) => (p.$ok ? '#047857' : '#B91C1C')};
`;

const Hint = styled.p`
  margin: 0 14px 14px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  line-height: 1.5;
`;

const MetaLine = styled.div`
  margin: 0 14px 10px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

const ImpactList = styled.ul`
  margin: 0 14px 14px;
  padding-left: 18px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

/**
 * 사업구조화관리 — 예산과목구조관리와 동일 레이아웃 (사업 레벨 체계 정의·확정)
 * 수지구분·예산구분·적용시작/종료는 검색·확정 조건에서 사용하지 않음.
 */
const BudgetProjectStructureInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const year = String(new Date().getFullYear());

  const searchDefaults = {
    tenantCode,
    fiscalYear: year,
    status: '작성중',
  };

  const methods = useForm({ mode: 'onSubmit', defaultValues: { ...searchDefaults } });
  const { getValues, setValue } = methods;
  const [statusLabel, setStatusLabel] = useState('작성중');
  const [meta, setMeta] = useState({ confirmedBy: '', confirmedAt: '', versionNo: 1 });

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const isLocked = statusLabel === '확정';

  const columns = useMemo(
    () => [
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 100, options: YN_OPTIONS }),
      hiCol.edComboColumn({ header: '레벨', field: 'levelCode', size: 100, options: LEVEL_CODE_OPTIONS }),
      hiCol.edTextColumn({ header: '레벨명', field: 'levelName', size: 110, required: true }),
      hiCol.edComboColumn({ header: '최종레벨', field: 'finalYn', size: 100, options: YN_OPTIONS }),
      hiCol.edComboColumn({ header: '코드규칙', field: 'codeRule', size: 120, options: RULE_OPTIONS }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      levelNo: 1,
      useYn: 'Y',
      levelCode: 'LV1',
      levelName: '',
      finalYn: 'N',
      codeRule: '자체',
    },
  });

  const [validationTick, setValidationTick] = useState(0);
  const liveRows = useMemo(() => gridRef.current?.getData?.() || [], [validationTick, grid.loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const validation = useMemo(() => validateProjectStructure(liveRows), [liveRows]);

  const refreshValidation = useCallback(() => {
    setValidationTick((n) => n + 1);
  }, []);

  useEffect(() => {
    grid.onAfterDataLoaded = () => refreshValidation();
    grid.onAfterSaveCell = () => refreshValidation();
  }, [grid, refreshValidation]);

  useEffect(() => {
    ['useYn', 'levelCode', 'levelName', 'finalYn', 'codeRule'].forEach((f) => {
      grid.setColOption?.(f, { readOnly: isLocked });
    });
  }, [grid, isLocked]);

  const dimsFromForm = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: v.tenantCode,
      fiscalYear: v.fiscalYear,
      purpose: STRUCTURE_PURPOSE,
    };
  }, [getValues]);

  const applyPayloadOrPreset = useCallback(async () => {
    const dims = dimsFromForm();
    let data;
    try {
      data = await loadProjectStructurePayload(dims);
    } catch (e) {
      showAlert(e?.message || '사업구조 조회에 실패했습니다.', 'error');
      return;
    }
    if (data?.rows?.length) {
      grid.setData(sanitizeProjectStructureRows(data.rows));
      const st = data.status || '작성중';
      setStatusLabel(st);
      setValue('status', st);
      setMeta({
        confirmedBy: data.confirmedBy || '',
        confirmedAt: data.confirmedAt || '',
        versionNo: data.versionNo || 1,
      });
    } else {
      grid.setData(cloneProjectPreset());
      setStatusLabel('작성중');
      setValue('status', '작성중');
      setMeta({ confirmedBy: '', confirmedAt: '', versionNo: 1 });
    }
    refreshValidation();
  }, [dimsFromForm, grid, setValue, refreshValidation]);

  useEffect(() => {
    applyPayloadOrPreset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(async () => {
    await applyPayloadOrPreset();
    showAlert('사업구조를 불러왔습니다.', 'success');
  }, [applyPayloadOrPreset]);

  const buildPayload = useCallback(
    (status, extra = {}) => {
      const rows = sanitizeProjectStructureRows(gridRef.current?.getData?.() || []);
      const v = validateProjectStructure(rows);
      const dims = dimsFromForm();
      const y = String(dims.fiscalYear || year);
      return {
        dims,
        purpose: dims.purpose,
        status,
        versionNo: Number(extra.versionNo ?? meta.versionNo) || 1,
        startDate: `${y}-01-01`,
        endDate: `${y}-12-31`,
        rows,
        usedLevels: sanitizeProjectStructureRows(v.used),
        finalLevelName: v.finalLevelName,
        applyLevelNo: v.applyLevelNo,
        applyLevelCode: v.applyLevelCode,
        applyLevelName: v.applyLevelName,
        levelColumns: buildBudgetLevelColumnFields(v.used),
        confirmedBy: extra.confirmedBy ?? meta.confirmedBy ?? null,
        confirmedAt: extra.confirmedAt ?? meta.confirmedAt ?? null,
        unconfirmHistory: extra.unconfirmHistory,
        savedAt: new Date().toISOString(),
        savedBy: user?.id || '',
      };
    },
    [dimsFromForm, user, meta, year]
  );

  const handleSave = useCallback(async () => {
    if (isLocked) {
      showAlert('확정된 구조는 저장할 수 없습니다. 확정해제 후 수정하세요.', 'warning');
      return;
    }
    const rows = sanitizeProjectStructureRows(gridRef.current?.getData?.() || []);
    const v = validateProjectStructure(rows);
    if (!v.hasNames) {
      showAlert('사용중인 레벨명은 필수입니다.', 'warning');
      return;
    }
    if (v.finalOne && !v.finalIsDeepest) {
      showAlert('최종레벨은 사용 중인 가장 깊은 레벨에 지정해야 합니다. (편성~지출 적용 깊이)', 'warning');
      return;
    }
    const payload = buildPayload('작성중');
    try {
      await saveProjectStructurePayload(dimsFromForm(), payload);
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
      return;
    }
    grid.setData(payload.rows);
    setValue('status', '작성중');
    showAlert('저장되었습니다.', 'success');
    refreshValidation();
  }, [buildPayload, dimsFromForm, grid, isLocked, setValue, refreshValidation]);

  const handleConfirm = useCallback(async () => {
    const rows = sanitizeProjectStructureRows(gridRef.current?.getData?.() || []);
    const v = validateProjectStructure(rows);
    if (!v.canConfirm) {
      const msg = !v.finalIsDeepest
        ? '최종레벨은 사용 중인 가장 깊은 레벨(LV)에 1건만 지정하세요. 그 단계가 편성~지출까지 사용하는 사업 깊이입니다.'
        : '레벨(LV) 연속 · 최종레벨 1건 · 레벨명을 확인하세요.';
      showAlert(msg, 'warning');
      return;
    }
    const dims = dimsFromForm();
    const ok = await showConfirm(
      `사업구조를 확정할까요?\n회계연도: ${dims.fiscalYear}\n체계: ${v.previewText}\n적용 사업 깊이: ${v.applyLevelName}(${v.applyLevelCode})\n${v.applyHint}\n\n확정 후 레벨 변경이 잠기며 사업등록·편성~지출에 반영됩니다.`,
      'warning'
    );
    if (!ok) return;
    const confirmedAt = new Date().toISOString();
    const confirmedBy = user?.id || user?.name || '';
    let payload;
    try {
      const prev = await loadProjectStructurePayload(dims);
      payload = buildPayload('확정', {
        confirmedAt,
        confirmedBy,
        versionNo: prev?.versionNo || meta.versionNo || 1,
        unconfirmHistory: prev?.unconfirmHistory || [],
      });
      await saveProjectStructurePayload(dims, payload);
    } catch (e) {
      showAlert(e?.message || '확정에 실패했습니다.', 'error');
      return;
    }
    grid.setData(payload.rows);
    setStatusLabel('확정');
    setValue('status', '확정');
    setMeta({ confirmedBy, confirmedAt, versionNo: payload.versionNo });
    showAlert(`사업구조가 확정되었습니다.\n${v.previewText}\n${v.applyHint}`, 'success');
    refreshValidation();
  }, [buildPayload, dimsFromForm, grid, setValue, refreshValidation, user, meta.versionNo]);

  const handleUnconfirm = useCallback(async () => {
    if (!isLocked) {
      showAlert('확정 상태가 아닙니다.', 'info');
      return;
    }
    const dims = dimsFromForm();
    const gate = canUnconfirmProjectStructure(dims);
    if (!gate.ok) {
      showAlert(gate.reason, 'warning');
      return;
    }
    const ok = await showConfirm('확정을 해제하고 작성중으로 되돌릴까요?', 'warning');
    if (!ok) return;
    let payload;
    try {
      const prev = (await loadProjectStructurePayload(dims)) || {};
      const history = [
        ...(prev.unconfirmHistory || []),
        {
          at: new Date().toISOString(),
          by: user?.id || '',
          prevConfirmedAt: prev.confirmedAt,
          prevConfirmedBy: prev.confirmedBy,
        },
      ];
      payload = buildPayload('작성중', {
        confirmedAt: null,
        confirmedBy: null,
        unconfirmHistory: history,
        versionNo: prev.versionNo || 1,
      });
      await saveProjectStructurePayload(dims, payload);
    } catch (e) {
      showAlert(e?.message || '확정해제에 실패했습니다.', 'error');
      return;
    }
    grid.setData(payload.rows);
    setStatusLabel('작성중');
    setValue('status', '작성중');
    setMeta({ confirmedBy: '', confirmedAt: '', versionNo: payload.versionNo });
    showAlert('확정이 해제되었습니다.', 'success');
    refreshValidation();
  }, [isLocked, dimsFromForm, buildPayload, grid, user, setValue, refreshValidation]);

  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrateProjects, setMigrateProjects] = useState([]);

  const handleOpenMigrate = useCallback(async () => {
    const dims = dimsFromForm();
    let data;
    try {
      data = await loadProjectStructurePayload(dims);
    } catch (e) {
      showAlert(e?.message || '사업구조 조회에 실패했습니다.', 'error');
      return;
    }
    if (!data?.rows?.length) {
      showAlert('이관할 사업구조가 없습니다. 먼저 조회·저장하세요.', 'warning');
      return;
    }
    setMigrateProjects(listProjectsForMigrate(dims));
    setMigrateOpen(true);
  }, [dimsFromForm]);

  const handleMigrateConfirm = useCallback(
    async ({ mode, selectedKeys }) => {
      const dims = dimsFromForm();
      let result;
      try {
        const src = await loadProjectStructurePayload(dims);
        if (src?.status !== '확정') {
          const ok = await showConfirm(
            '현재 연도 사업구조가 확정되지 않았습니다. 그래도 차년도이관 할까요?',
            'warning'
          );
          if (!ok) return;
        }
        result = await migrateProjectYear(dims, {
          userId: user?.id || '',
          mode: mode || 'all',
          selectedKeys: selectedKeys || [],
        });
      } catch (e) {
        setMigrateOpen(false);
        showAlert(e?.message || '차년도이관에 실패했습니다.', 'error');
        return;
      }
      setMigrateOpen(false);
      if (!result.ok) {
        showAlert(result.reason, 'warning');
        return;
      }
      setValue('fiscalYear', result.nextDims.fiscalYear);
      await applyPayloadOrPreset();
      showAlert(result.message || '차년도이관이 완료되었습니다.', 'success');
    },
    [dimsFromForm, user, setValue, applyPayloadOrPreset]
  );

  const handleResetPreset = useCallback(async () => {
    if (isLocked) {
      showAlert('확정된 구조는 프리셋을 적용할 수 없습니다.', 'warning');
      return;
    }
    const ok = await showConfirm('기본 사업구조 프리셋(정책 > 단위 > 세부)으로 되돌릴까요?', 'warning');
    if (!ok) return;
    grid.setData(cloneProjectPreset());
    setStatusLabel('작성중');
    setValue('status', '작성중');
    refreshValidation();
  }, [grid, setValue, refreshValidation, isLocked]);

  const handleAddRow = useCallback(() => {
    if (isLocked) {
      showAlert('확정된 구조는 행을 추가할 수 없습니다.', 'warning');
      return;
    }
    const n = Math.min((gridRef.current?.getData?.()?.length || 0) + 1, 8);
    grid.handleAdd({
      levelNo: n,
      levelCode: `LV${n}`,
      codeRule: '자체',
      finalYn: 'N',
    });
  }, [grid, isLocked]);

  const handleDeleteRow = useCallback(() => {
    if (isLocked) {
      showAlert('확정된 구조는 행을 삭제할 수 없습니다.', 'warning');
      return;
    }
    grid.handleDelete();
  }, [grid, isLocked]);

  const getExportData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getExportColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '사업구조화',
    getData: getExportData,
    getDataColumns: getExportColumns,
    joinKeys: ['levelCode'],
    order: 0,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '사업구조화' });

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: CommonCss.w90,
          labelWidth: SEARCH_LABEL_W,
          min: 2000,
          max: 2099,
        },
        { name: 'status', label: '상태', type: 'text', width: CommonCss.w70, labelWidth: SEARCH_LABEL_W, readOnly: true },
      ],
    ],
    []
  );

  const actionButtons = [
    { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
    { id: 'add', label: '추가', icon: '➕', onClick: handleAddRow, hotkey: 'F8' },
    { id: 'delete', label: '삭제', icon: '❌', onClick: handleDeleteRow, hotkey: 'F9', variant: 'danger' },
    { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    excelButton,
  ];

  const extraButtons = [
    { id: 'confirm', label: '구조확정', onClick: handleConfirm },
    { id: 'unconfirm', label: '확정해제', onClick: handleUnconfirm },
    { id: 'migrateYear', label: '차년도이관', onClick: handleOpenMigrate },
    { id: 'preset', label: '프리셋', onClick: handleResetPreset },
  ];

  const columnPreview = validation.used.length
    ? buildBudgetLevelColumnFields(validation.used)
        .map((c) => c.header)
        .join(' · ')
    : '-';

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup
          buttons={actionButtons}
          extraButtons={extraButtons}
          loading={grid.loading}
        />
        <CollapsibleSearchPanel
          storageKey="budget-project-structure"
          prefsUserId={prefsUserId}
          summary="회계연도·구조 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Hint style={{ marginTop: 0 }}>{PROJECT_STRUCTURE_HELP}</Hint>

      <Layout>
        <Panel>
          <PanelHead>
            레벨구조 그리드 — 최종레벨
            {isLocked ? ' · [확정잠금]' : ''}
          </PanelHead>
          <div style={{ flex: 1, minHeight: 280, padding: 8 }}>
            <GridExportFocusScope gridId={GRID_ID}>
              <HiGridComponent
                ref={gridRef}
                columns={columns}
                height="100%"
                showRowNumber
                rowNumberSize={56}
                showModColumn
                {...grid.contextMenuProps}
                enableHeaderMenu
                enableSelectionStats
                enableFreeze
                enableColumnFilters
                enableQuickExport
                exportFileName="사업구조화"
                gridId={GRID_ID}
                prefsUserId={prefsUserId}
                colOptions={grid.colOptions}
              />
            </GridExportFocusScope>
          </div>
        </Panel>

        <Panel>
          <PanelHead>검증 / 미리보기</PanelHead>
          <PreviewPath>{validation.previewText}</PreviewPath>
          <MetaLine>
            버전 {meta.versionNo || 1}
            {meta.confirmedBy
              ? ` · 확정자 ${meta.confirmedBy} · ${String(meta.confirmedAt).replace('T', ' ').slice(0, 19)}`
              : ' · 미확정'}
            {isLocked ? ' · 확정잠금' : ''}
          </MetaLine>
          <Hint style={{ marginBottom: 8 }}>
            <strong>적용 사업 깊이</strong>
            <br />
            {validation.applyHint}
          </Hint>
          <CheckList>
            <CheckItem>
              <span>레벨(LV) 연속</span>
              <Badge $ok={validation.continuous}>{validation.continuous ? '정상' : '오류'}</Badge>
            </CheckItem>
            <CheckItem>
              <span>최종레벨 1건</span>
              <Badge $ok={validation.finalOne}>{validation.finalOne ? '정상' : '오류'}</Badge>
            </CheckItem>
            <CheckItem>
              <span>최종=최심층</span>
              <Badge $ok={validation.finalIsDeepest}>{validation.finalIsDeepest ? '정상' : '오류'}</Badge>
            </CheckItem>
            <CheckItem>
              <span>구조확정 가능</span>
              <Badge $ok={validation.canConfirm}>{validation.canConfirm ? '가능' : '불가'}</Badge>
            </CheckItem>
            <CheckItem>
              <span>상태</span>
              <Badge $ok={statusLabel === '확정'}>{statusLabel}</Badge>
            </CheckItem>
          </CheckList>
          <CardTitle style={{ margin: '0 14px 6px', fontSize: '0.85rem' }}>하위 화면 컬럼 미리보기</CardTitle>
          <Hint>
            사업등록에 표시될 계층 컬럼:
            <br />
            <strong>{columnPreview}</strong>
            <br />
            편성~지출에서 실제 선택·저장하는 사업 단위:
            <br />
            <strong>
              {validation.applyLevelName
                ? `${validation.applyLevelName} (${validation.applyLevelCode})`
                : '-'}
            </strong>
          </Hint>
          <CardTitle style={{ margin: '0 14px 6px', fontSize: '0.85rem' }}>확정 시 영향 범위</CardTitle>
          <ImpactList>
            {PROJECT_DOWNSTREAM_IMPACT_SCREENS.map((s) => (
              <li key={s.id}>
                {s.label}
                {validation.applyLevelName ? ` → ${validation.applyLevelName} 사업` : ''}
              </li>
            ))}
          </ImpactList>
        </Panel>
      </Layout>
      {excelDialog}
      <YearMigrateDialog
        open={migrateOpen}
        kind="project"
        dims={dimsFromForm()}
        projectRows={migrateProjects}
        structureStatus={statusLabel}
        onClose={() => setMigrateOpen(false)}
        onConfirm={handleMigrateConfirm}
      />
    </PageContainer>
  );
};

const BudgetProjectStructure = () => (
  <GridExportProvider>
    <BudgetProjectStructureInner />
  </GridExportProvider>
);

export default BudgetProjectStructure;
